import { Loader2, XCircle, CheckCircle, RotateCcw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"

interface TranscriptionJob {
  id: number
  file_name: string
  status: string
  submitted_at: string
  completed_at: string | null
  destination_path: string
  audio_duration: number | null
}

interface TranscriptionStatusScreenProps {
  processing: TranscriptionJob[]
  failed: TranscriptionJob[]
  completed: TranscriptionJob[]
  onRetry: (id: number) => void
}

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function JobRow({ job, icon, badge, action }: {
  job: TranscriptionJob
  icon: React.ReactNode
  badge: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      {icon}
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium">{job.file_name}</p>
        <p className="text-xs text-muted-foreground">{timeAgo(job.submitted_at)}</p>
      </div>
      {badge}
      {action}
    </div>
  )
}

export function TranscriptionStatusScreen({ processing, failed, completed, onRetry }: TranscriptionStatusScreenProps) {
  const hasJobs = processing.length > 0 || failed.length > 0 || completed.length > 0

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6" data-testid="transcription-status">
      <h2 className="text-xl font-semibold">Transcriptions</h2>

      {!hasJobs && (
        <p className="text-sm text-muted-foreground">No transcription jobs yet.</p>
      )}

      {/* Processing */}
      {processing.length > 0 && (
        <section data-testid="processing-section">
          <h3 className="mb-3 text-sm font-medium text-muted-foreground">In progress</h3>
          <div className="space-y-2">
            {processing.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                icon={<Loader2 className="h-5 w-5 animate-spin text-primary" />}
                badge={<Badge variant="secondary">Processing</Badge>}
              />
            ))}
          </div>
        </section>
      )}

      {/* Failed */}
      {failed.length > 0 && (
        <section data-testid="failed-section">
          <h3 className="mb-3 text-sm font-medium text-destructive">Failed</h3>
          <div className="space-y-2">
            {failed.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                icon={<XCircle className="h-5 w-5 text-destructive" />}
                badge={<Badge variant="destructive">Failed</Badge>}
                action={
                  <Button size="sm" variant="ghost" onClick={() => onRetry(job.id)} data-testid={`retry-${job.id}`}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <section data-testid="completed-section">
          <h3 className="mb-3 text-sm font-medium text-muted-foreground">Completed</h3>
          <div className="space-y-2">
            {completed.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                icon={<CheckCircle className="h-5 w-5 text-green-500" />}
                badge={<Badge variant="outline">Complete</Badge>}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
